/**
 * Permission API — v2 POST endpoint.
 *
 *   POST {API_BASE_URL}/v2/users/composer/permission/{userId}
 *   body: PermissionFilterPayload (built via the generic DSL)
 *
 * Hard cutover from v1 — no automatic fallback. If v2 fails the error
 * surfaces all the way up so the caller (PermissionContext, dependency
 * loaders) can display a clear failure state to the user.
 *
 * Every transition is logged at `info` / `warn` level so the dev console
 * tells you exactly what fired, which payload was sent, and what the
 * server returned.
 *
 * Goes through `apiClient` so the 401-refresh interceptor still handles
 * expired access tokens automatically.
 */
import apiClient from '../config/axios.config';
import {
  PermissionFilterOptions,
  PermissionFilterPayload,
  buildPermissionPayload,
} from './permissions/permissionFilterBuilder';
import type {
  PermissionResponse,
  PermissionResponseData,
} from '../types/permissions';
import { logger } from '../utils/logger';

const V2_ENDPOINT = (userId: string): string =>
  `/api/v2/users/composer/permission/${userId}`;

const summariseFilters = (
  filters: PermissionFilterOptions,
): Record<string, unknown> => ({
  domainIds: filters.domainIds?.length ?? 0,
  routeIds: filters.routeIds?.length ?? 0,
  operationIds: filters.operationIds?.length ?? 0,
  workflowIds: filters.workflowIds?.length ?? 0,
});

/**
 * Names of every permission bucket the backend may ship. The presence
 * of ANY of these keys at the right depth is enough to treat the
 * response as a valid permission payload — the server legitimately
 * omits buckets the user has no records for.
 */
const PERMISSION_BUCKET_KEYS: ReadonlyArray<string> = [
  'routePermissions',
  'domainPermissions',
  'domainAttributePermissions',
  'operationPermissions',
  'stageSectionAttributePermissions',
  'workflowPermissions',
  'stageDomainAttributePermissions',
];

const hasAnyPermissionKey = (obj: unknown): boolean => {
  if (!obj || typeof obj !== 'object') return false;
  const record = obj as Record<string, unknown>;
  return PERMISSION_BUCKET_KEYS.some((key) => record[key] !== undefined);
};

/**
 * Normalise the v2 response into the canonical `{ permissions: { … } }`
 * shape every consumer expects.
 *
 * Backend variants we accept:
 *   A) { data: { permissions: { <anyBucket>, … } } }
 *   B) { data: { <anyBucket>, … } }
 *   C) { permissions: { <anyBucket>, … } }
 *   D) { <anyBucket>, … }
 *
 * Returns `{ permissions: {} }` on a 200 with no recognised keys — we
 * never throw on a successful HTTP response. Empty buckets propagate
 * through the resolver as "no entry → HIDE per cell", which is the
 * correct UX. Throwing here used to cascade into
 * `PermissionContext.status = 'error'` and break the dep loader.
 */
function normalisePermissionBody(raw: unknown): PermissionResponseData {
  const fallback: PermissionResponseData = {
    permissions: {
      routePermissions: {},
      domainPermissions: {},
      domainAttributePermissions: {},
    },
  };
  if (!raw || typeof raw !== 'object') return fallback;
  const obj = raw as Record<string, unknown>;

  // Shape A — data.permissions.<anyBucket>
  const dataA = obj.data as { permissions?: unknown } | undefined;
  if (dataA?.permissions && hasAnyPermissionKey(dataA.permissions)) {
    return dataA as PermissionResponseData;
  }
  // Shape B — data.<anyBucket>
  if (obj.data && hasAnyPermissionKey(obj.data)) {
    return {
      permissions: obj.data as PermissionResponseData['permissions'],
    };
  }
  // Shape C — permissions.<anyBucket>
  if (obj.permissions && hasAnyPermissionKey(obj.permissions)) {
    return obj as unknown as PermissionResponseData;
  }
  // Shape D — <anyBucket> at root
  if (hasAnyPermissionKey(obj)) {
    return {
      permissions: obj as unknown as PermissionResponseData['permissions'],
    };
  }
  return fallback;
}

export async function fetchPermissions(
  userId: string,
  filters: PermissionFilterOptions = {},
): Promise<PermissionResponse> {
  const payload: PermissionFilterPayload = buildPermissionPayload(filters);
  logger.info('[permissionApi] POST v2', {
    endpoint: V2_ENDPOINT(userId),
    filters: summariseFilters(filters),
  });
  try {
    const response = await apiClient.post<unknown>(V2_ENDPOINT(userId), payload);
    const normalised = normalisePermissionBody(response.data);

    // Diagnostic — surfaces the actual top-level keys + bucket sizes the
    // server sent, so a future shape variation is immediately visible in
    // the dev console without needing a debugger session.
    const raw = response.data as Record<string, unknown> | null;
    const topKeys = raw && typeof raw === 'object' ? Object.keys(raw) : [];
    logger.info('[permissionApi] v2 success', {
      status: response.status,
      topKeys,
      buckets: {
        route: Object.keys(normalised.permissions?.routePermissions ?? {}).length,
        domain: Object.keys(normalised.permissions?.domainPermissions ?? {}).length,
        domainAttr: Object.keys(
          normalised.permissions?.domainAttributePermissions ?? {},
        ).length,
      },
    });

    return { data: normalised };
  } catch (err) {
    logger.error(
      '[permissionApi] v2 failed',
      err instanceof Error
        ? { message: err.message, name: err.name }
        : err,
    );
    throw err;
  }
}

// Back-compat alias — existing `PermissionContext` calls `getPermissionData`.
// The `minimizeResponse` flag is ignored under v2 (the filter envelope
// supersedes it).
export async function getPermissionData(
  userId: string,
  _minimizeResponse: boolean = true,
): Promise<PermissionResponse> {
  return fetchPermissions(userId);
}
